import { useState, useEffect, useCallback } from 'react';
import '../styles/AdminCompras.css';

const ITEM_VACIO = { descripcion: '', cantidad: 1, precio_unitario: 0 };
const FORM_VACIO = { fecha: '', proveedor: '', items: [{ ...ITEM_VACIO }], notas: '' };

function hoy() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function isoHoy() {
  return new Date().toISOString().split('T')[0];
}

function displayAIso(dd) {
  if (!dd) return '';
  const [d, m, y] = dd.split('/');
  return `${y}-${m}-${d}`;
}

function isoADisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function calcTotal(items) {
  return items.reduce((s, i) => s + (Number(i.cantidad)||0) * (Number(i.precio_unitario)||0), 0);
}

export default function AdminCompras({ isOpen, onClose }) {
  const [vista, setVista] = useState('lista'); // 'lista' | 'form' | 'confirmar'
  const [compras, setCompras] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  // Form nueva/editar compra
  const [form, setForm] = useState({ ...FORM_VACIO, fecha: hoy() });
  const [editandoId, setEditandoId] = useState(null);

  // Modal confirmar compra
  const [compraAConfirmar, setCompraAConfirmar] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ monto_final: '', nro_factura: '' });
  const [confirmError, setConfirmError] = useState('');

  // Detalle expandido en lista
  const [expandida, setExpandida] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    fetch('/api/compras')
      .then(r => r.json())
      .then(data => { setCompras(Array.isArray(data) ? data : []); setCargando(false); })
      .catch(() => { setError('Error al cargar compras'); setCargando(false); });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    cargar();
    fetch('/api/proveedores')
      .then(r => r.json())
      .then(data => setProveedores(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [isOpen, cargar]);

  if (!isOpen) return null;

  const mostrarExito = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(''), 3000);
  };

  // ── Form helpers ──
  const setItem = (idx, campo, valor) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [campo]: valor };
      return { ...f, items };
    });
  };

  const agregarItem = () => setForm(f => ({ ...f, items: [...f.items, { ...ITEM_VACIO }] }));

  const quitarItem = (idx) => setForm(f => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));

  const abrirNueva = () => {
    setForm({ ...FORM_VACIO, fecha: hoy() });
    setEditandoId(null);
    setError('');
    setVista('form');
  };

  const abrirEditar = (c) => {
    setForm({
      fecha: c.fecha || hoy(),
      proveedor: c.proveedor || '',
      items: c.items?.length ? c.items : [{ ...ITEM_VACIO }],
      notas: c.notas || '',
    });
    setEditandoId(c._id);
    setError('');
    setVista('form');
  };

  const guardarForm = async () => {
    setError('');
    if (!form.items.some(i => i.descripcion.trim())) {
      setError('Agregá al menos un ítem con descripción');
      return;
    }
    const payload = {
      ...form,
      items: form.items.filter(i => i.descripcion.trim()),
    };
    try {
      const res = await fetch(
        editandoId ? `/api/compras/${editandoId}` : '/api/compras',
        {
          method: editandoId ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error al guardar'); return; }
      cargar();
      mostrarExito(editandoId ? 'Compra actualizada' : 'Compra creada');
      setVista('lista');
    } catch {
      setError('Error de conexión');
    }
  };

  const eliminarCompra = async (id) => {
    if (!window.confirm('¿Eliminar esta compra?')) return;
    await fetch(`/api/compras/${id}`, { method: 'DELETE' });
    cargar();
  };

  // ── Confirmar compra ──
  const abrirConfirmar = (c) => {
    setCompraAConfirmar(c);
    setConfirmForm({ monto_final: c.total_estimado || '', nro_factura: '' });
    setConfirmError('');
    setVista('confirmar');
  };

  const guardarConfirmacion = async () => {
    setConfirmError('');
    if (!confirmForm.monto_final) { setConfirmError('Ingresá el monto final'); return; }
    try {
      const res = await fetch(`/api/compras/${compraAConfirmar._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmar: true, ...confirmForm }),
      });
      const data = await res.json();
      if (!res.ok) { setConfirmError(data.error || 'Error'); return; }
      cargar();
      mostrarExito('Compra confirmada');
      setVista('lista');
      setCompraAConfirmar(null);
    } catch {
      setConfirmError('Error de conexión');
    }
  };

  const comprasFiltradas = filtroEstado
    ? compras.filter(c => c.estado === filtroEstado)
    : compras;

  const pendientes = compras.filter(c => c.estado === 'pendiente').length;

  // ── RENDER ──
  return (
    <div className="ac-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ac-panel">

        {/* Header */}
        <div className="ac-header">
          <div className="ac-header-left">
            {vista !== 'lista' && (
              <button className="ac-btn-back" onClick={() => setVista('lista')}>← Volver</button>
            )}
            <h2 className="ac-titulo">
              {vista === 'lista' && <>📋 Compras {pendientes > 0 && <span className="ac-badge">{pendientes}</span>}</>}
              {vista === 'form' && (editandoId ? '✎ Editar compra' : '+ Nueva compra')}
              {vista === 'confirmar' && '✓ Confirmar compra'}
            </h2>
          </div>
          <div className="ac-header-right">
            {vista === 'lista' && (
              <button className="ac-btn-nuevo" onClick={abrirNueva}>+ Nueva compra</button>
            )}
            <button className="ac-btn-cerrar" onClick={onClose}>✕</button>
          </div>
        </div>

        {exito && <div className="ac-exito">{exito}</div>}

        {/* ── LISTA ── */}
        {vista === 'lista' && (
          <div className="ac-body">
            <div className="ac-filtros">
              {['', 'pendiente', 'confirmada'].map(e => (
                <button
                  key={e}
                  className={`ac-filtro-btn${filtroEstado === e ? ' activo' : ''}`}
                  onClick={() => setFiltroEstado(e)}
                >
                  {e === '' ? 'Todas' : e === 'pendiente' ? 'Pendientes' : 'Confirmadas'}
                </button>
              ))}
            </div>

            {cargando && <p className="ac-cargando">Cargando...</p>}

            {!cargando && comprasFiltradas.length === 0 && (
              <p className="ac-vacio">No hay compras{filtroEstado ? ` ${filtroEstado}s` : ''}.</p>
            )}

            <div className="ac-lista">
              {comprasFiltradas.map(c => (
                <div key={c._id} className={`ac-item ${c.estado}`}>
                  <div className="ac-item-header" onClick={() => setExpandida(expandida === c._id ? null : c._id)}>
                    <div className="ac-item-info">
                      <span className={`ac-estado-badge ${c.estado}`}>
                        {c.estado === 'pendiente' ? '⏳ Pendiente' : '✓ Confirmada'}
                      </span>
                      <span className="ac-item-fecha">{c.fecha}</span>
                      {c.proveedor && <span className="ac-item-proveedor">{c.proveedor}</span>}
                    </div>
                    <div className="ac-item-totales">
                      <span className="ac-item-total-est">
                        Est. ${(c.total_estimado || 0).toLocaleString('es-AR')}
                      </span>
                      {c.estado === 'confirmada' && c.monto_final != null && (
                        <span className="ac-item-total-final">
                          Final: ${Number(c.monto_final).toLocaleString('es-AR')}
                        </span>
                      )}
                      <span className="ac-chevron">{expandida === c._id ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {expandida === c._id && (
                    <div className="ac-item-detalle">
                      <table className="ac-tabla-items">
                        <thead>
                          <tr>
                            <th>Descripción</th>
                            <th>Cant.</th>
                            <th>P. Unit.</th>
                            <th>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(c.items || []).map((it, i) => (
                            <tr key={i}>
                              <td>{it.descripcion}</td>
                              <td>{it.cantidad}</td>
                              <td>${Number(it.precio_unitario).toLocaleString('es-AR')}</td>
                              <td>${(Number(it.cantidad) * Number(it.precio_unitario)).toLocaleString('es-AR')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {c.notas && <p className="ac-item-notas">📝 {c.notas}</p>}

                      {c.estado === 'confirmada' && (
                        <div className="ac-confirmacion-info">
                          {c.nro_factura && <span>🧾 Factura: <strong>{c.nro_factura}</strong></span>}
                          {c.fecha_confirmacion && <span>📅 Confirmada: {c.fecha_confirmacion}</span>}
                        </div>
                      )}

                      <div className="ac-item-acciones">
                        {c.estado === 'pendiente' && (
                          <>
                            <button className="ac-btn-confirmar" onClick={() => abrirConfirmar(c)}>
                              ✓ Confirmar compra
                            </button>
                            <button className="ac-btn-editar" onClick={() => abrirEditar(c)}>
                              ✎ Editar
                            </button>
                          </>
                        )}
                        <button className="ac-btn-eliminar" onClick={() => eliminarCompra(c._id)}>
                          🗑 Eliminar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── FORM ── */}
        {vista === 'form' && (
          <div className="ac-body">
            {error && <p className="ac-error">{error}</p>}

            <div className="ac-form-grid">
              <div className="ac-campo">
                <label>Fecha</label>
                <input
                  type="date"
                  value={displayAIso(form.fecha)}
                  max={isoHoy()}
                  onChange={e => setForm(f => ({ ...f, fecha: isoADisplay(e.target.value) }))}
                />
              </div>
              <div className="ac-campo">
                <label>Proveedor</label>
                <select
                  value={form.proveedor}
                  onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))}
                >
                  <option value="">Sin proveedor</option>
                  {proveedores.map(p => (
                    <option key={p._id} value={p.nombre}>{p.nombre}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tabla de ítems */}
            <div className="ac-items-seccion">
              <div className="ac-items-header">
                <span className="ac-items-titulo">Lista de materiales</span>
                <button className="ac-btn-add-item" onClick={agregarItem}>+ Agregar ítem</button>
              </div>

              <div className="ac-items-tabla">
                <div className="ac-items-thead">
                  <span>Descripción</span>
                  <span>Cant.</span>
                  <span>P. Unit. $</span>
                  <span>Subtotal</span>
                  <span></span>
                </div>
                {form.items.map((item, idx) => (
                  <div key={idx} className="ac-items-fila">
                    <input
                      type="text"
                      placeholder="Ej: Tornillos 4mm"
                      value={item.descripcion}
                      onChange={e => setItem(idx, 'descripcion', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={item.cantidad}
                      onChange={e => setItem(idx, 'cantidad', e.target.value)}
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.precio_unitario}
                      onChange={e => setItem(idx, 'precio_unitario', e.target.value)}
                    />
                    <span className="ac-item-subtotal">
                      ${((Number(item.cantidad)||0) * (Number(item.precio_unitario)||0)).toLocaleString('es-AR')}
                    </span>
                    <button
                      className="ac-btn-quitar-item"
                      onClick={() => quitarItem(idx)}
                      disabled={form.items.length === 1}
                    >✕</button>
                  </div>
                ))}
              </div>

              <div className="ac-total-estimado">
                Total estimado: <strong>${calcTotal(form.items).toLocaleString('es-AR')}</strong>
              </div>
            </div>

            <div className="ac-campo">
              <label>Notas</label>
              <textarea
                rows={3}
                placeholder="Observaciones opcionales..."
                value={form.notas}
                onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
              />
            </div>

            <div className="ac-form-acciones">
              <button className="ac-btn-cancelar" onClick={() => setVista('lista')}>Cancelar</button>
              <button className="ac-btn-guardar" onClick={guardarForm}>
                {editandoId ? 'Guardar cambios' : 'Crear compra'}
              </button>
            </div>
          </div>
        )}

        {/* ── CONFIRMAR ── */}
        {vista === 'confirmar' && compraAConfirmar && (
          <div className="ac-body">
            <div className="ac-confirmar-info">
              <p>Vas a confirmar la compra del <strong>{compraAConfirmar.fecha}</strong>
                {compraAConfirmar.proveedor && <> a <strong>{compraAConfirmar.proveedor}</strong></>}.
              </p>
              <p className="ac-confirmar-estimado">
                Total estimado: ${(compraAConfirmar.total_estimado || 0).toLocaleString('es-AR')}
              </p>
            </div>

            {confirmError && <p className="ac-error">{confirmError}</p>}

            <div className="ac-form-grid">
              <div className="ac-campo">
                <label>Monto final pagado *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={confirmForm.monto_final}
                  onChange={e => setConfirmForm(f => ({ ...f, monto_final: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="ac-campo">
                <label>N° de factura / remito</label>
                <input
                  type="text"
                  placeholder="Ej: 0001-00012345"
                  value={confirmForm.nro_factura}
                  onChange={e => setConfirmForm(f => ({ ...f, nro_factura: e.target.value }))}
                />
              </div>
            </div>

            <div className="ac-form-acciones">
              <button className="ac-btn-cancelar" onClick={() => setVista('lista')}>Cancelar</button>
              <button className="ac-btn-confirmar-ok" onClick={guardarConfirmacion}>
                ✓ Confirmar compra
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
